import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import Section from '@components/Section';
import { BadgeAddressIcon } from '@components/Icons';
import { useTheme } from '@hooks/use-theme';
import { useAuthContext } from '@hooks/use-auth-context';
import styles from './Location.module.scss';
import { ThemeName } from '@app/themes';
import { ContactLocation, selectLocation } from '@reducers/data/locations';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLocation } from '@actions/locations';
import Contact from '@models/Contact';

function Map({ src }) {
  const [isLoaded, setIsLoaded] = useState(false);

  const onImageLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const img = new window.Image();
    img.src = src;
    img.onload = onImageLoad;

    return () => {
      img.onload = null;
    };
  }, [src]);

  return (
    <div
      className={cn(styles.image, isLoaded && styles.imageLoaded)}
      style={{ backgroundImage: `url("${src}")` }}
    />
  );
}

function Clock({ timezone }) {
  const [time, setTime] = useState(null);

  const updateTime = useCallback(() => {
    if (!timezone) {
      return null;
    }

    setTime(
      new Date().toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        timeZoneName: 'short',
      }),
    );
  }, [timezone]);

  useEffect(() => {
    const interval = setInterval(updateTime, 5000);
    updateTime();

    return () => clearInterval(interval);
  });

  return <>{time}</>;
}

const mapThemeThemeMap: Record<ThemeName, string> = {
  'light': 'ckzzxdbhk002q14nrroy6snek',
  'dark-graphite': 'clptv538n00dn01r5dk3da0zp',
  'dark-black': 'ckzzxa2y6003915oigly056mc',
};

const coordsToMapURL = (theme: ThemeName, coords: ContactLocation) => {
  if (!coords) {
    return null;
  }

  const { latitude, longitude } = coords;
  const style = mapThemeThemeMap[theme];

  const marker = `https://assets.clay.earth/maps/${
    theme === 'light' ? 'light' : 'dark'
  }-map-dot-sm.png`;

  return `https://api.mapbox.com/styles/v1/zhamed/${style}/static/url-${encodeURIComponent(
    marker,
  )}(${longitude},${latitude})/${longitude + 0.3},${
    latitude + 0.3
  },7.9,0,0/650x650@2x?access_token=sk.eyJ1IjoiemhhbWVkIiwiYSI6ImNrenp6cDV0cDBmOHUzbG5mZHB1b2R0eWUifQ.MT1z8nS3FOumiWNvH0eZPw&attribution=false&logo=false&before_layer=poi-label`;
};

export default function Location({ contact }: { contact: Contact }) {
  const { user } = useAuthContext();
  const dispatch = useDispatch();
  const locationString = contact.locationString;
  const [theme] = useTheme();
  const contactLocation = useSelector(selectLocation(locationString));
  const mapURL = coordsToMapURL(theme, contactLocation);

  useEffect(() => {
    if (locationString && user) {
      dispatch(fetchLocation(locationString));
    }
  }, [locationString, user]);

  if (!contactLocation) {
    return null;
  }

  return (
    <Section className={cn(styles.container, user && styles.unauthenticatedUser)}>
      <div className={styles.location} key={locationString}>
        <div className={styles.content}>
          <Section.Title underline>
            {contact.primaryLocation?.address ? 'Address' : 'Location'}
          </Section.Title>

          <div className={styles.address}>
            <BadgeAddressIcon className={styles.icon} />
            <a
              target="_blank"
              href={`http://maps.apple.com/?q=${encodeURIComponent(contactLocation.location)}`}
            >
              {locationString}
            </a>
          </div>
        </div>

        <div className={styles.details}>
          <Clock timezone={contactLocation?.timezone} />
        </div>

        {mapURL && (
          <div className={styles.map}>
            <div className={styles.imageContainer}>
              <Map src={mapURL} />
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}
